// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {Component, OnInit} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderService } from 'src/app/header.service';
import { D3eService } from 'src/services/d3e-service';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormArray } from '@angular/forms';
import { IIntegration, PartnerTypes } from 'src/types/integrations';
import { NotificationService } from 'src/services/notification-service';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from 'src/app/dialogs/alert/alert.component';
import {SelectedOrgMethod} from '../../../../shared/selectedOrg';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-carbon-black',
    templateUrl: './carbon-black.component.html',
    styleUrls: ['./carbon-black.component.scss'],
    standalone: false
})
export class CarbonBlackComponent implements OnInit {
  private readonly PARTNER_ID = 1;
  formGroup: UntypedFormGroup;
  integration: IIntegration;
  integrationId: string = null;
  rules: UntypedFormArray;

  selectedOrgId: string;
  hasUserBeenSet = false;

  fAPI: string;
  fSIEM: string;
  fSecret: string;
  fValidate: string;
  fWActiveLock: string;
  fPActiveLock: string;
  fLockA: string;
  fLockB: string;
  fLockC: string;
  fAdmin: string;
  fCancel: string;
  fSave: string;
  fConfigCorrect: string;
  fConfigSaved: string;
  fChangesBeingSaved: string;
  fSaveError: string;
  fValidationFailed: string;

  title: string;
  subtitle: string;
  constructor(
    private route: ActivatedRoute,
    private d3eService: D3eService,
    public dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private headerService: HeaderService,
    private notification: NotificationService,
    private router: Router,
    private translate: TranslateService
  ) {
    this.translate.get(['INTC_API', 'INTC_SIEM', 'INTC_SECRET', 'INTC_VALIDATE', 'INTC_WACTIVELOCK',
      'INTC_PACTIVELOCK', 'INTC_LOCKA', 'INTC_LOCKB', 'INTC_LOCKC', 'INTC_ADMIN', 'CANCEL', 'SAVE_CAP', 'TITLE_CARBON_INT',
      'SUBTITLE_CARBON', 'CONFIG_CORRECT', 'CONFIG_SAVED', 'CHANGES_BEING_SAVED', 'REPORT_SAVE_ERROR',
      'ERROR_VALIDATION_FAILED']).subscribe(vals => {
          this.fAPI = vals['INTC_API'];
          this.fSIEM = vals['INTC_SIEM'];
          this.fSecret = vals['INTC_SECRET'];
          this.fValidate = vals['INTC_VALIDATE'];
          this.fWActiveLock = vals['INTC_WACTIVELOCK'];
          this.fPActiveLock = vals['INTC_PACTIVELOCK'];
          this.fLockA = vals['INTC_LOCKA'];
          this.fLockB = vals['INTC_LOCKB'];
          this.fLockC = vals['INTC_LOCKC'];
          this.fAdmin = vals['INTC_ADMIN'];
          this.fCancel = vals['CANCEL'];
          this.fSave = vals['SAVE_CAP'];
          this.fConfigCorrect = vals['CONFIG_CORRECT'];
          this.fConfigSaved = vals['CONFIG_SAVED'];
          this.fChangesBeingSaved = vals['CHANGES_BEING_SAVED'];
          this.fSaveError = vals['REPORT_SAVE_ERROR'];
          this.title = vals['TITLE_CARBON_INT'];
          this.subtitle = vals['SUBTITLE_CARBON'];
          this.fValidationFailed = vals['ERROR_VALIDATION_FAILED'];
          this.headerService.setTitle(this.title);
          this.headerService.setSubtitle(this.subtitle);
        });
    this.headerService.setIcon('mdi-sync');

    this.formGroup = this.formBuilder.group({
      api_url: ['', Validators.required],
      siem_api_id: ['', Validators.required],
      siem_secret_key: ['', Validators.required],
      watchlist_severity: ['', Validators.required],
      policy_severity: ['', Validators.required],
      lock_option: ['', Validators.required],
      admin_unlock: [{value: false, disabled: true}],
    });
  }

  ngOnInit() {

    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.selectedOrgId = value;
        if ( this.hasUserBeenSet ) {
            // TODO: launch the dirty flag window
            this.router.navigateByUrl('/integrations');
        }
        this.hasUserBeenSet = true;
    });

    this.route.params.subscribe(params => {
      this.integrationId = params.id;
      if (this.integrationId === 'new') {
        this.formGroup.controls['watchlist_severity'].setValue('7');
        this.formGroup.controls['policy_severity'].setValue('7');
      } else {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

        this.d3eService.get_integration(this.integrationId, selectedOrg)
          .pipe(untilDestroyed(this))
          .subscribe(integration => {
            if (integration.partner_id !== this.PARTNER_ID) {
              this.router.navigateByUrl('/integrations');
            }
            this.formGroup.controls['api_url'].setValue(integration.config.api_url);
            this.formGroup.controls['siem_api_id'].setValue(integration.config.siem_api_id);
            this.formGroup.controls['siem_secret_key'].setValue(integration.config.siem_secret_key);
            this.formGroup.controls['lock_option'].setValue(integration.config.lock_option || 'device_only');
            this.formGroup.controls['watchlist_severity'].setValue(integration.config.watchlist_severity || '7');
            this.formGroup.controls['policy_severity'].setValue(integration.config.policy_severity || '7');
            this.formGroup.controls['admin_unlock'].setValue(integration.config.admin_unlock);
            this.integration = integration;
        });
      }
    });
  }

  save() {
    this.notification.showUpdate(this.fChangesBeingSaved);
    const data = this.formGroup.value;
    const rules = [
      `.notifications[] | select(.threatHunterInfo.score >= ${data.watchlist_severity}) | select(.type | ascii_downcase == "threat_hunter") | {  cmp: .deviceInfo.deviceName, sensorType: 1002, enabled: true, timestamp: .eventTime, description: .summary, appName: .threatHunterInfo.indicators[0].applicationName, detail: .eventDescription, allowDeactivate: true, needsAuth: false}`,
      `.notifications[] | select(.threatInfo.score >= ${data.policy_severity}) | select(.type | ascii_downcase == "threat") | { cmp: .deviceInfo.deviceName, sensorType: 1002, enabled: true, timestamp: .eventTime, description: .summary, detail: .eventDescription, allowDeactivate: true, needsAuth: false}`
    ];
    if (this.integrationId === 'new') {
      const integration: IIntegration = {
        display_name: 'Carbon Black',
        enabled: true,
        partner_id: PartnerTypes.CARBON_BLACK,
        config: {
          api_url: data.api_url,
          siem_api_id: data.siem_api_id,
          siem_secret_key: data.siem_secret_key,
          watchlist_severity: data.watchlist_severity,
          policy_severity: data.policy_severity,
          rules: rules,
          admin_unlock: data.admin_unlock,
          lock_option: data.lock_option || false,
        }
      };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.add_integration(integration, selectedOrg)
        .subscribe(() => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    } else {
      this.integration.config = {
        api_url: data.api_url,
        siem_api_id: data.siem_api_id,
        siem_secret_key: data.siem_secret_key,
        watchlist_severity: data.watchlist_severity,
        policy_severity: data.policy_severity,
        rules: rules,
        admin_unlock: data.admin_unlock || false,
        lock_option: data.lock_option,
      };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.update_integration(this.integration, selectedOrg)
        .subscribe(() => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    }
  }

  reportSaveSuccess() {
    this.notification.showSuccess(this.fConfigSaved);
  }

  reportSaveError() {
    this.notification.showError(this.fSaveError);
  }

  goToIntegrations() {
    this.router.navigateByUrl('/integrations');
  }

  cancel() {
    if (this.formGroup.touched) {
      const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: {
          title: this.translate.instant('DISCARD_CHANGES'),
          content: this.translate.instant('DISCARD_CHANGES_CONTENT'),
          rejectBtnName: this.translate.instant('NO'),
          acceptBtnName: this.translate.instant('YES'),
          showRejectBtn: true,
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.goToIntegrations();
        }
      });
    } else {
      this.goToIntegrations();
    }
  }

  validate() {
    this.d3eService.validate_carbon_black_config(this.formGroup.value)
      .subscribe(response => {
        if (response.error) {
          this.notification.showError(this.fValidationFailed);
        } else if (response.data && !response.data.success) {
          this.notification.showError(this.fValidationFailed);
        } else if (response.status_code && response.status_code !== 200) {
          this.notification.showError(this.fValidationFailed);
        } else if (response.status_code && response.status_code === 200) {
          this.notification.showSuccess(this.fConfigCorrect);
        }
    });
  }
}
